import { useState, useEffect, useRef } from "react"
import { useQuery } from "@apollo/client"
import _ from "lodash"

import {
  GET_STAFF_ID_BY_MEMBERSHIP_ID,
  GET_USER,
  GET_STAFF_PROFILE_BOX,
} from "../pages/Staff/queries"

export default function useStaffProfileBoxData(staff_membership_id, firebase, userCtx) {
  const [currentStaffData, setCurrentStaffData] = useState(null)
  const [currentSelectedStaffUser, setCurrentSelectedStaffUser] = useState(null)
  const [telerecoveryEnabled, setTelerecoveryEnabled] = useState(false)
  const lastStaffContextRef = useRef(staff_membership_id || null)
  const lastProfileBoxRefreshVersionRef = useRef(
    userCtx.profileBoxRefreshVersion,
  )

  const {
    data: staffData,
    loading: staffLoading,
    refetch: refetchStaffData,
  } = useQuery(GET_STAFF_ID_BY_MEMBERSHIP_ID, {
    variables: { staff_membership_id },
    skip: !staff_membership_id,
    fetchPolicy: "network-only",
  })

  const { loading: profileBoxLoading, refetch: refetchStaffProfileBox } = useQuery(
    GET_STAFF_PROFILE_BOX,
    {
      variables: {
        organization_id: staffData?.staffDataByStaffMembershipID?.organization_id,
        staff_id: staffData?.staffDataByStaffMembershipID?.staff_id,
      },
      fetchPolicy: "no-cache",
      skip:
        !staffData?.staffDataByStaffMembershipID?.staff_id ||
        !staffData?.staffDataByStaffMembershipID?.organization_id,
      onCompleted: (data) => {
        setCurrentStaffData(data.getStaffProfileBox || [])
      },
      onError: () => {
        setCurrentStaffData(null)
      },
    }
  )

  const {
    loading: selectedStaffUserLoading,
    refetch: refetchSelectedStaffUser,
  } = useQuery(GET_USER, {
    variables: {
      staff_id: staffData?.staffDataByStaffMembershipID?.staff_id,
      organization_id: staffData?.staffDataByStaffMembershipID?.organization_id,
    },
    fetchPolicy: "no-cache",
    skip:
      !staffData?.staffDataByStaffMembershipID?.staff_id ||
      !staffData?.staffDataByStaffMembershipID?.organization_id,
    onCompleted: (data) => {
      setCurrentSelectedStaffUser(data?.user || null)
    },
    onError: () => {
      setCurrentSelectedStaffUser(null)
    },
  })

  useEffect(() => {
    // Keep the profile card in sync when either:
    // 1) selected staff membership changes, or
    // 2) an explicit profile-box refresh is requested after a form save.
    const staffMembershipId = staff_membership_id || null
    const hasProfileBoxRefreshChanged =
      lastProfileBoxRefreshVersionRef.current !==
      userCtx.profileBoxRefreshVersion
    const hasContextChanged = lastStaffContextRef.current !== staffMembershipId
    const noStaffSelected = _.isEmpty(staff_membership_id)

    if (noStaffSelected) {
      // No active staff selection: clear staff-scoped state to avoid stale details.
      lastStaffContextRef.current = null
      lastProfileBoxRefreshVersionRef.current = userCtx.profileBoxRefreshVersion
      setCurrentStaffData(null)
      setCurrentSelectedStaffUser(null)
      return
    }

    if (hasContextChanged || hasProfileBoxRefreshChanged) {
      // Keep current card data mounted while refreshing in the background
      // to avoid visible pop-out/pop-in during staff switches.
      refetchStaffData({ staff_membership_id })

      if (
        staffData?.staffDataByStaffMembershipID?.staff_id &&
        staffData?.staffDataByStaffMembershipID?.organization_id
      ) {
        // If staff ID/org are already known, refetch dependent queries immediately.
        refetchStaffProfileBox({
          organization_id: staffData.staffDataByStaffMembershipID.organization_id,
          staff_id: staffData.staffDataByStaffMembershipID.staff_id,
        })

        // Refetch the selected staff user to ensure the latest user details are loaded.
        refetchSelectedStaffUser({
          organization_id: staffData.staffDataByStaffMembershipID.organization_id,
          staff_id: staffData.staffDataByStaffMembershipID.staff_id,
        })
      }
    }

    // Snapshot current inputs so future runs can detect real changes.
    lastStaffContextRef.current = staffMembershipId
    lastProfileBoxRefreshVersionRef.current = userCtx.profileBoxRefreshVersion
  }, [
    refetchSelectedStaffUser,
    refetchStaffData,
    refetchStaffProfileBox,
    staffData,
    staff_membership_id,
    userCtx.profileBoxRefreshVersion,
  ])

  useEffect(() => {
    const microservicesRef = firebase.db
      .collection("enterprise-config")
      .doc(userCtx.session.user.enterprise_id)
      .collection("organizations")
      .doc(userCtx.session.user.organization_id)
      .collection("microservices")

    const unsubscribe = microservicesRef.onSnapshot(
      (snapshot) => {
        if (!snapshot.empty) {
          const microservicesData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))

          setTelerecoveryEnabled(
            microservicesData.find((service) => service.id === "1")?.enable
          )
        } else {
          setTelerecoveryEnabled(false)
          console.log("No microservices found!")
        }
      },
      (error) => {
        setTelerecoveryEnabled(false)
        console.log("Error listening to microservices collection:", error)
      }
    )

    return () => unsubscribe()
  }, [firebase.db, userCtx.session.user.enterprise_id, userCtx.session.user.organization_id])

  return {
    staffData,
    currentStaffData,
    currentSelectedStaffUser,
    telerecoveryEnabled,
    profileBoxLoading,
    selectedStaffUserLoading,
    staffLoading,
  }
}
